import { Fragment, useEffect, useState } from "react";
import { createTaskApi } from "../../api-helpers/TaskApiHelper";
import { GridDataRequest, GridDataResponse, TaskDetailDto, TaskTypes } from "tasks-experience-client";
import { formatDate } from "../../helper/dateFormatter";
import OffCanvas from "../../components/offCanvas";
import AddUpdateTask from "../tasks/AddUpdateTask";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { createPatientApi } from "../../api-helpers/PatientApiHelper";
import { PatientPersonalInfo } from "patients-experience-client";
import Spinner from "../../components/Spinner";
import Lookups from "../../constants/Lookups";
import { createCommonUtilsApi, createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";
import { PriorityBadge } from "../../renderers/PriorityBadge";
import { useParams } from "react-router-dom";
import { WorkflowConstants } from "../../helper/WorkflowConstants";
import { WorkflowState } from "common-util-experience-client";
import { createUsersApi } from "../../api-helpers/UserApiHelper";
import { createGridApi } from "../../api-helpers/GridApiHelper";

interface SampleProps {
    type: 'Task' | 'Order';
    scope: 'Patient' | 'Global';
    coachId: string | undefined;
    orderId?: string;
}
export const TaskOrderCard = (props: SampleProps) => {
    const lookupApi = createCommonUtilsApi();
    const taskApi = createTaskApi();
    const gridApi = createGridApi();
    const workflowApi = createWorkflowApi();
    const patientApi = createPatientApi();
    const usersApi = createUsersApi();
    const { patientId } = useParams<{ patientId: string }>();
    const [gridData, setGridData] = useState<any[]>([]);
    const [selectedTaskId, setSelectedTaskId] = useState<string>();
    const [selectedTask, setSelectedTask] = useState<TaskDetailDto>({} as TaskDetailDto);
    const [offCanvasShow, setOffCanvasShow] = useState(false);
    const [offCanvasTitle, setOffCanvasTitle] = useState("");
    const [showToast, setShowToast] = useState(false);
    const [patientProfile, setPatientProfile] = useState<PatientPersonalInfo>();
    const [loading, setLoading] = useState(false);
    const [showPlaceholder, setShowPlaceholder] = useState(false);
    const [error, setError] = useState<string>('');
    const [priorities, setPriorities] = useState<{ [key: string]: string }>({});
    const [users, setUsers] = useState<{ [key: string]: string }>({});
    const [openStates, setOpenStates] = useState<WorkflowState[]>([]);
    const [toastConfig, setToastConfig] = useState<ToastConfig>({
        type: "success" as ErrorTypes,
        title: "",
        icon: "",
    });

    const PLACEHOLDER = <span className="placeholder-wave">
        <span className="placeholder w-100"></span>
    </span>;

    const loadData = async () => {
        setError('');
        setGridData([]);
        setShowPlaceholder(true);
        try {
            const filters: GridDataRequest = {
                filters: [
                    ...(props.scope === 'Patient' ? [{
                        propertyName: "patientId",
                        operator: "equals",
                        propertyValue: patientId
                    }] : []),
                    ...(props.coachId && props.coachId !== undefined ? [{
                        propertyName: "assignedUserId",
                        operator: "equals",
                        propertyValue: props.coachId
                    }] : []),
                    ...(props.scope !== 'Patient' && props.type === "Order" ? [{
                        propertyName: "taskTypeId",
                        operator: "in",
                        propertyValue: [props.orderId !== '' ? props.orderId : null]
                    }] : props.scope !== 'Patient' && props.type !== "Order" ? [{
                        propertyName: "taskTypeId",
                        operator: "not-in",
                        propertyValue: [props.orderId !== '' ? props.orderId : null]
                    }] : []),
                    ...(openStates && openStates.length ? [{
                        propertyName: "wfStateId",
                        operator: "in",
                        propertyValue: openStates.map(p => p.stateId)
                    }] : []),
                    ...(props.scope !== 'Patient' ? [{
                        propertyName: "dueAt",
                        operator: "less-than-equals",
                        propertyValue: new Date()
                    }] : []),
                ],
                sortCriteria: [
                    {
                        propertyName: "dueAt",
                        descending: false
                    }
                ],
                startRow: 0,
                endRow: 100,
            };

            const response = await gridApi.gridControllerGetTaskGridData(filters);
            const filtered = response.data?.data;
            setGridData(filtered);
            if (filtered.length === 0) {
                setError('No Data Found');
            } else {
                setError('');
            }
        } catch (error) {
            setError(props.type === 'Task' ? 'Error while fetching tasks' : 'Error while fetching orders');
        } finally {
            setShowPlaceholder(false);
        }
    };

    const handleUpdateTask = async (row: any) => {
        setSelectedTaskId(row.taskId);
        await loadTaskDetails(row.taskId);
        await getPatientProfile(row.patientId);
        handleOffCanvasShow();
    }

    const getPatientProfile = async (patientId: string) => {
        setLoading(true);
        try {
            const response =
                await patientApi.patientsControllerGetPatientPersonalInfo(patientId!);
            setPatientProfile(response.data);
        } catch (error) {
            console.error("Error fetching Patients:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOffCanvasShow = () => {
        setOffCanvasTitle("Edit Task/Order");
        setOffCanvasShow(!offCanvasShow);
    };

    const handlerAddUpdateTaskForm = async (toasterConfig: ToastConfig) => {
        setToastConfig(toasterConfig);
        setShowToast(true);
        loadData();
    };

    const loadTaskDetails = async (id: string) => {
        setLoading(true);
        try {
            const response = await taskApi.taskControllerFindOne(id);
            await setSelectedTask(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const getOpenStateIds = async () => {
            setLoading(true);
            setOpenStates([]);
            try {
                const response = await workflowApi.workflowControllerGetStatesForWorkflow(WorkflowConstants.taskWorkflowType);
                const states = response.data.filter((state: WorkflowState) => state.isEnd === false);
                setOpenStates(states);
            } catch (ex: any) {
            } finally {
                setLoading(false);
            }
        };
        getOpenStateIds();
    }, []);

    useEffect(() => {
        if(props.orderId && openStates.length) {
            loadData();
        }
    }, [props.orderId, props.coachId, openStates]);

    const getPriorityById = async (data: string) => {
        return (await lookupApi.lookupControllerFindOne(Lookups.TASK_PRIORITY,data)).data.name;
    }

    const getUserById = async (data: string) => {
        const response = await usersApi.usersControllerFindOne(data);
        return response.data.firstName + ' ' + response.data.lastName;
    }

    useEffect(() => {
        const fetchPriorities = async () => {
            setShowPlaceholder(true);
            const priorityPromises = gridData.map(async (row: any) => {
                const priority = await getPriorityById(row.priorityId);
                return { [row.priorityId]: priority };
            }) || [];
            const priorityResults = await Promise.all(priorityPromises);
            setPriorities(Object.assign({}, ...priorityResults));
            setShowPlaceholder(false);
        };

        const fetchUsers = async () => {
            setShowPlaceholder(true);
            const userPromises = gridData.map(async (row: any) => {
                const user = await getUserById(row.assignedUserId);
                return { [row.assignedUserId]: user };
            }) || [];
            const userResults = await Promise.all(userPromises);
            setUsers(Object.assign({}, ...userResults));
            setShowPlaceholder(false);
        };

        if (gridData.length !== 0) {
            fetchPriorities();
            fetchUsers();
        }
    }, [gridData]);

    const getFormatDate = (dueOn: string) => {
        const dueDate = new Date(dueOn);
        const currentDate = new Date();

        const dueDateWithoutTime = new Date(Date.UTC(dueDate.getUTCFullYear(), dueDate.getUTCMonth(), dueDate.getUTCDate()));
        const currentDateWithoutTime = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate()));

        const formatDate = (date: Date) => {
            const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
            const day = date.getUTCDate().toString().padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${month}/${day}/${year}`;
        };

        if (dueDateWithoutTime.getTime() < currentDateWithoutTime.getTime()) {
            return (
                <span className="badge badge-orange-light text-danger ms-2" style={{ fontWeight: '600' }}>
                    Overdue {formatDate(dueDateWithoutTime)}
                </span>
            );
        } else if (dueDateWithoutTime.getTime() > currentDateWithoutTime.getTime()) {
            return (
                <span className="d-block fs-11 text-light">
                     {formatDate(dueDateWithoutTime)}
                </span>
            );
        } else {
            return <span className="d-block fs-11 text-light">Due Today</span>;
        }
    };

    const openInNewTab = (url: string) => {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
    }

    return (<>
        <div className="row">
            <Toaster
                show={showToast}
                onClose={() => setShowToast(false)}
                type={toastConfig.type}
                title={toastConfig.title}
                message={toastConfig.message}
                icon={toastConfig.icon}
            />
        </div>
        {loading && <Spinner />}
        {error === '' && !showPlaceholder && gridData.length !== 0 ? gridData.map((row, idx) => <Fragment key={idx} >
            <div className="d-flex p-3">
            <div className="flex-fill">
                <div className="d-flex">
                    <div className="flex-fill">
                        {patientId && <><div className="fs-12 text-light mb-1">
                            Assignee: {users[row.assignedUserId]}
                        </div>
                            <a className="fw-bold" href="#">{row.taskSubtype}</a>
                        </>}

                        {!patientId && <>
                            <a className="fw-bold text-secondary" href="#" onClick={() => {openInNewTab(`/patients/${row.patientId}/summary`);}}>{row.patientName}</a>
                            <div className="fs-13 text-light my-1">
                                {row.taskSubtype}
                            </div>
                        </>}
                    </div>
                    <div className="ps-4">
                        <span className="d-block fs-11 text-light">{getFormatDate(row.dueAt)}</span>
                    </div>
                </div>
                <div className="d-flex align-items-center justify-content-between fs-12 mt-1">
  {/* Display the priority badge */}
  <PriorityBadge params={priorities[row.priorityId] || "Loading..."} />

  {/* Action link for updating the task */}
  <div>
    <a
      className="a-link text-info fs-12"
      href="#"
      onClick={() => handleUpdateTask(row)}
    >
      View
    </a>
  </div>
</div>

            </div>
        </div>
        <hr className="m-0" /></Fragment>
    ) : ''}


        {error !== '' && !showPlaceholder && gridData.length === 0 ? <div className="col-12"><div className='p-4'>
            <div className="alert alert-danger border-0" role="alert">
                {error}
            </div>
        </div></div> : ''}

        {error === '' && showPlaceholder && gridData.length === 0 ? Array.from({ length: 3 }).map((_, idx) => (
            <Fragment key={idx}>
                <div className="p-3">
                    <div className="d-flex">
                        <div className="flex-fill">
                            <span className="text-light fs-11">
                                <div className="w-50">
                                    {PLACEHOLDER}
                                    {PLACEHOLDER}
                                </div>
                            </span>
                        </div>
                        <div className="ps-4 w-20">
                            {PLACEHOLDER}
                        </div>
                    </div>
                </div>
                <hr className="m-0" />
            </Fragment>
        )) : ''
        }

        <OffCanvas
            title={offCanvasTitle}
            offCanvasShow={offCanvasShow}
            setOffCanvasShow={setOffCanvasShow}
            offCanvasComp='updateTask'
        >
            <AddUpdateTask
                patient={patientProfile}
                setOffCanvasShow={setOffCanvasShow}
                onSubmitTask={handlerAddUpdateTaskForm}
                selectedTask={selectedTask}
                isUpdate={true}
                taskId={selectedTaskId}
            />
        </OffCanvas>
    </>);
}
